import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaUser, FaList, FaTimes, FaChevronLeft, FaChevronRight, FaPaintBrush } from 'react-icons/fa';
import FullScreenImageViewer from '../popups/FullScreenImageViewer';
import { useNavigate } from 'react-router-dom';
import { TropheeInclusSportCard, TropheeInclusSportCardHeader, TropheeInclusSportCardBody, OsarcMagCard, OsarcMagImage, OsarcMagContent, OsarcMagTitle, OsarcMagDate, FestivalPicantinCard, FestivalPicantinCardHeader, FestivalPicantinCardBody } from '../activities/ActivityCardsStyles';
import { createUrlFriendlyString } from '../../utils/slug';

// Utilitaire de slug centralisé

// Composant simplifié pour les magazines Osarc - juste image, titre et date
const SimpleOsarcMagCard = ({ pdfUrl, title, date, image, onClick }) => {
  return (
    <OsarcMagCard onClick={pdfUrl ? onClick : undefined} style={{ cursor: pdfUrl ? 'pointer' : 'default' }}>
      <OsarcMagImage>
        {image ? (
          <img 
            src={image} 
            alt={title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '8px 8px 0 0'
            }}
            onError={(e) => {
              const imgEl = e.currentTarget || e.target;
              if (imgEl && imgEl.style) {
                imgEl.style.display = 'none';
              }
              const placeholder = imgEl && imgEl.nextElementSibling ? imgEl.nextElementSibling : null;
              if (placeholder && placeholder.style) {
                placeholder.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div style={{
          width:'100%',
          height:'100%',
          display: image ? 'none' : 'flex',
          flexDirection:'column',
          alignItems:'center',
          justifyContent:'center',
          backgroundColor:'#f5f5f5',
          color:'#666',
          padding:'20px',
          textAlign:'center'
        }}>
          <div style={{fontSize:'3rem',marginBottom:'10px'}}>📄</div>
          <div>{pdfUrl ? 'Cliquez pour ouvrir le PDF' : 'Magazine à venir prochainement'}</div>
        </div>
      </OsarcMagImage>
      <OsarcMagContent>
        <OsarcMagTitle>{title}</OsarcMagTitle>
        <OsarcMagDate>{date}</OsarcMagDate>
      </OsarcMagContent>
    </OsarcMagCard>
  );
};

const Card = styled.div`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 200px; // Hauteur fixe pour toutes les images
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
  }
`;

const CardBody = styled.div`
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardCategory = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 8px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #333;
  line-height: 1.4;
`;

const CardText = styled.p`
  margin: 10px 0 0;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
  display: ${props => props.$isTrophee ? 'none' : 'block'};
  ${props => props.style}
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const PopupContent = styled.div`
  background: white;
  border-radius: 10px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
`;

const PopupCloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: #888;
  font-size: 1.5rem;
  cursor: pointer;
  &:hover { color: #333; }
`;

const PopupTitle = styled.h2`
  font-size: 1.3rem;
  color: #132e57;
  margin-bottom: 20px;
  text-align: center;
`;

const PopupImage = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  margin: 0 0 20px;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: #f5f5f5;
  }
`;

const PopupInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  color: #555;
  font-size: 1rem;
  svg { margin-right: 12px; color: #4a6bce; min-width: 18px; }
`;

const PopupDescription = styled.div`
  color: #555;
  font-size: 1rem;
  line-height: 1.6;
  margin: 25px 0;
`;

// Ajouter les keyframes CSS globalement
if (typeof document !== 'undefined' && !document.getElementById('timeline-animations')) {
  const style = document.createElement('style');
  style.id = 'timeline-animations';
  style.textContent = `
    @keyframes timelineGrow {
      from { transform: scaleY(0); }
      to { transform: scaleY(1); }
    }
    
    @keyframes fadeInUp {
      from { 
        opacity: 0; 
        transform: translateY(30px) scale(0.9); 
      }
      to { 
        opacity: 1; 
        transform: translateY(0px) scale(1); 
      }
    }
    
    @keyframes bubbleIn {
      0% { 
        opacity: 0; 
        transform: scale(0) rotate(-180deg); 
      }
      50% { 
        transform: scale(1.1) rotate(-90deg); 
      }
      100% { 
        opacity: 1; 
        transform: scale(1) rotate(0deg); 
      }
    }
  `;
  document.head.appendChild(style);
}

// Composant pour afficher les lauréats Picantins avec design Timeline
const PicantinLaureatsDisplay = ({ laureats, design, title }) => {
  const [visibleItems, setVisibleItems] = React.useState(new Set());
  const observerRef = React.useRef();

  React.useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleItems(prev => new Set([...prev, index]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px -50px 0px'
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  React.useEffect(() => {
    const items = document.querySelectorAll('[data-timeline-item]');
    items.forEach((item) => {
      if (observerRef.current) {
        observerRef.current.observe(item);
      }
    });

    // Faire apparaître automatiquement les 2 premiers éléments
    setTimeout(() => {
      setVisibleItems(prev => new Set([...prev, 0, 1]));
    }, 100);

    return () => {
      if (observerRef.current) {
        items.forEach((item) => {
          observerRef.current.unobserve(item);
        });
      }
    };
  }, [laureats]);

  if (!laureats || laureats.length === 0) return null;

  // Design Timeline Verticale Moderne - Chronologie élégante
  return (
    <div style={{ 
      position: 'relative',
      marginTop: 10
    }}>
      <div style={{
        position: 'absolute',
        left: 30,
        top: 0,
        bottom: 0,
        width: 3,
        background: 'linear-gradient(180deg, #1a73e8 0%, #132e57 100%)',
        borderRadius: 2,
        transform: 'scaleY(0)',
        transformOrigin: 'top',
        animation: 'timelineGrow 1.5s ease-out 0.3s forwards'
      }} />
      

      
      {laureats.sort((a,b)=>b.annee-a.annee).map((l,idx)=>{
        const isVisible = visibleItems.has(idx);
        const delay = idx * 100; // Délai progressif pour chaque élément
        
        return (
        <div 
          key={idx} 
          data-timeline-item
          data-index={idx}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            marginBottom: 24,
            position: 'relative',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0px) scale(1)' : 'translateX(-80px) scale(0.8)',
            transition: `all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms`,
            filter: isVisible ? 'blur(0px)' : 'blur(3px)'
          }}>
          <div 
            title={`Année ${l.annee}`}
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1a73e8, #4285f4)',
              border: '4px solid white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              color: 'white',
              zIndex: 2,
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1) rotate(0deg)' : 'scale(0) rotate(-180deg)',
              animation: isVisible ? `bubbleIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) ${delay + 200}ms forwards` : 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.1) rotate(5deg)';
              e.target.style.boxShadow = '0 8px 20px rgba(26, 115, 232, 0.4)';
              // Animation de la vignette parente
              const vignette = e.target.parentElement.querySelector('[data-vignette]');
              if (vignette) {
                vignette.style.transform = 'translateX(10px) scale(1.02)';
                vignette.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1) rotate(0deg)';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              // Retour normal de la vignette parente
              const vignette = e.target.parentElement.querySelector('[data-vignette]');
              if (vignette) {
                vignette.style.transform = 'translateX(0) scale(1)';
                vignette.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
              }
            }}>
            {l.annee}
          </div>
          
          <div data-vignette style={{
            background: 'transparent',
            borderRadius: 12,
            padding: 20,
            marginLeft: 20,
            flex: 1,
            boxShadow: 'none',
            border: 'none',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0px) scale(1)' : 'translateX(50px) scale(0.9)',
            animation: isVisible ? `fadeInUp 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay + 400}ms forwards` : 'none'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateX(10px) scale(1.02)';
            e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
            // Animation de la bulle année
            const bubble = e.target.parentElement.querySelector('[title*="Année"]');
            if (bubble) {
              bubble.style.transform = 'scale(1.1) rotate(5deg)';
              bubble.style.boxShadow = '0 8px 20px rgba(26, 115, 232, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateX(0) scale(1)';
            e.target.style.boxShadow = 'none';
            // Retour normal de la bulle année
            const bubble = e.target.parentElement.querySelector('[title*="Année"]');
            if (bubble) {
              bubble.style.transform = 'scale(1) rotate(0deg)';
              bubble.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }
          }}>
            {/* Image de fond avec overlay */}
            {l.image && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${l.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.1,
                zIndex: 0
              }} />
            )}
            
            {/* Contenu principal */}
            <div style={{ 
              position: 'relative', 
              zIndex: 1,
              display: 'flex', 
              alignItems: 'center',
              gap: 16
            }}>
              {/* Photo de profil */}
              <div style={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                background: l.image ? `url(${l.image})` : `url(https://i.pravatar.cc/100?img=${(idx % 70) + 1})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                border: '2px solid #e2e8f0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: 'white'
              }}>
              </div>
             
              {/* Informations */}
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap'
                }}>
                  <h3 style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: 700, 
                    color: '#132e57', 
                    margin: 0,
                    lineHeight: 1.3
                  }}>
                    {l.nom}
                  </h3>
                  <span style={{
                    fontSize: '0.85rem',
                    color: '#1a73e8',
                    fontWeight: 600,
                    background: 'transparent',
                    padding: '0',
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4
                  }}>
                    🏟️ <span>{l.club || l.clubs || 'Club non défini'}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
};

// Composant pour afficher le podium Club du mois Décathlon
const ClubDuMoisPodiumDisplay = ({ podium, title }) => {
  const [visibleItems, setVisibleItems] = React.useState(new Set());

  // Nettoyage des logs bruyants
  React.useEffect(() => {
    // no-op
  }, [podium]);
  const observerRef = React.useRef();

  React.useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleItems(prev => new Set([...prev, index]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px -50px 0px'
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  React.useEffect(() => {
    const items = document.querySelectorAll('[data-podium-item]');
    items.forEach((item) => {
      if (observerRef.current) {
        observerRef.current.observe(item);
      }
    });

    // Faire apparaître automatiquement tous les éléments du podium
    setTimeout(() => {
      setVisibleItems(prev => new Set([...prev, 0, 1, 2]));
    }, 100);

    return () => {
      if (observerRef.current) {
        items.forEach((item) => {
          observerRef.current.unobserve(item);
        });
      }
    };
  }, [podium]);

  if (!podium || podium.length === 0) return null;

  const getPodiumHeight = (position) => {
    switch(position) {
      case 1: return 120; // Or - le plus haut
      case 2: return 100; // Argent - moyen
      case 3: return 80;  // Bronze - le plus bas
      default: return 60;
    }
  };

  const getPodiumColor = (position) => {
    switch(position) {
      case 1: return 'linear-gradient(135deg, #FFD700, #FFA500)'; // Or
      case 2: return 'linear-gradient(135deg, #C0C0C0, #A8A8A8)'; // Argent
      case 3: return 'linear-gradient(135deg, #CD7F32, #B8860B)'; // Bronze
      default: return 'linear-gradient(135deg, #e3e8f4, #c5d0e6)';
    }
  };

  const getMedalEmoji = (position) => {
    switch(position) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏆';
    }
  };

  // Trier le podium pour afficher dans l'ordre : 2ème, 1er, 3ème (effet podium visuel)
  const sortedPodium = [...podium].sort((a, b) => {
    const order = { 2: 0, 1: 1, 3: 2 };
    return order[a.position] - order[b.position];
  });

  return (
    <div style={{
      padding: '30px 20px',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      borderRadius: 15,
      margin: '20px 0',
      position: 'relative'
    }}>
      <div style={{
        textAlign: 'center',
        marginBottom: 40
      }}>
        <h3 style={{
          fontSize: '1.4rem',
          fontWeight: 700,
          color: '#132e57',
          margin: '0 0 8px 0'
        }}>
          🏆 Club du mois Decathlon
        </h3>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: 20,
        maxWidth: 600,
        margin: '0 auto'
      }}>
        {sortedPodium.map((club, idx) => {
          const isVisible = visibleItems.has(idx);
          const delay = club.position === 1 ? 400 : club.position === 2 ? 200 : 600; // 1er apparaît en dernier pour l'effet
          
          return (
            <div
              key={club.position}
              data-podium-item
              data-index={idx}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0px) scale(1)' : 'translateY(50px) scale(0.8)',
                transition: `all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms`,
                filter: isVisible ? 'blur(0px)' : 'blur(2px)'
              }}
            >
              {/* Logo du club - Cliquable */}
              <div 
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: club.logo ? '#ffffff' : 'linear-gradient(135deg, #1a73e8, #4285f4)',
                  border: `4px solid ${club.position === 1 ? '#FFD700' : club.position === 2 ? '#C0C0C0' : '#CD7F32'}`,
                  marginBottom: 15,
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: 'white',
                  animation: isVisible ? `bubbleIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) ${delay + 200}ms forwards` : 'none',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onClick={(e) => {
                  // Pas besoin de stopPropagation dans une popup
                  const urlPath = createUrlFriendlyString(club.nom);
                  window.open(`/clubs/${urlPath}`, '_blank');
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 12px 25px rgba(0,0,0,0.25)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
                }}
                title={`Voir la fiche de ${club.nom}`}
              >
                {/* Si logo disponible, afficher l'image */}
                {club.logo ? (
                  <img 
                    src={club.logo} 
                    alt={`Logo ${club.nom}`}
                    style={{
                      width: '70%',
                      height: '70%',
                      objectFit: 'contain',
                      borderRadius: '50%',
                      pointerEvents: 'none'
                    }}
                    onError={(e) => {
                      // Si l'image ne charge pas, masquer l'img et afficher la lettre
                      const imgEl = e.currentTarget || e.target;
                      if (imgEl && imgEl.style) {
                        imgEl.style.display = 'none';
                      }
                      const parent = imgEl ? imgEl.parentElement : null;
                      if (parent) {
                        parent.innerHTML = `<span style="font-size: 1.5rem; font-weight: bold; color: white; text-shadow: 0 1px 3px rgba(0,0,0,0.3); pointer-events: none;">${club.nom.charAt(0).toUpperCase()}</span>`;
                        if (parent.style) {
                          parent.style.background = 'linear-gradient(135deg, #1a73e8, #4285f4)';
                        }
                      }
                    }}
                  />
                ) : (
                  /* Si pas de logo, afficher la première lettre */
                  <span style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold', 
                    color: 'white',
                    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    pointerEvents: 'none'
                  }}>
                    {club.nom.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* Informations du club */}
              <div style={{
                textAlign: 'center',
                marginBottom: 15,
                minHeight: 60
              }}>
                <h4 
                  style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#132e57',
                    margin: '0 0 4px 0',
                    lineHeight: 1.2,
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    textDecoration: 'none'
                  }}
                  onClick={(e) => {
                    // Pas besoin de stopPropagation dans une popup
                    const urlPath = createUrlFriendlyString(club.nom);
                    window.open(`/clubs/${urlPath}`, '_blank');
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#4a6bce';
                    e.target.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#132e57';
                    e.target.style.textDecoration = 'none';
                  }}
                  title={`Voir la fiche de ${club.nom}`}
                >
                  {club.nom}
                </h4>
                <p style={{
                  fontSize: '0.85rem',
                  color: '#64748b',
                  margin: '0 0 8px 0',
                  fontWeight: 500
                }}>
                  {club.sport}
                </p>

              </div>

              {/* Marche du podium */}
              <div style={{
                width: 100,
                height: getPodiumHeight(club.position),
                background: getPodiumColor(club.position),
                borderRadius: '8px 8px 0 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingTop: 15,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                position: 'relative',
                border: '2px solid rgba(255,255,255,0.3)'
              }}>
                <div style={{
                  fontSize: '2rem',
                  marginBottom: 8
                }}>
                  {getMedalEmoji(club.position)}
                </div>
                
                {/* Effet de brillance */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 100%)',
                  borderRadius: '8px 8px 0 0'
                }} />
              </div>
            </div>
          );
        })}
      </div>


    </div>
  );
};

// Petit carrousel simple pour les images TIS (réutilisé vignette et popup)
const TISCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const validImages = Array.isArray(images) ? images.filter(Boolean) : [];

  // Défilement automatique
  useEffect(() => {
    if (validImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex(prev => prev === validImages.length - 1 ? 0 : prev + 1);
      setTimeout(() => setIsTransitioning(false), 400);
    }, 4000); // Change toutes les 4 secondes
    
    return () => clearInterval(interval);
  }, [currentIndex, validImages.length]);

  const prev = (e) => {
    if (e) e.stopPropagation();
    if (isTransitioning || validImages.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev === 0 ? validImages.length - 1 : prev - 1);
    setTimeout(() => setIsTransitioning(false), 400);
  };
  const next = (e) => {
    if (e) e.stopPropagation();
    if (isTransitioning || validImages.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev === validImages.length - 1 ? 0 : prev + 1);
    setTimeout(() => setIsTransitioning(false), 400);
  };

  if (validImages.length === 0) return null;

  // Style pour les boutons du carrousel TIS (identique aux actualités)
  const carouselButtonBaseStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translate3d(0,-50%,0)',
    transformOrigin: 'center center',
    background: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    border: 'none',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 20,
    transition: 'background 0.3s ease, transform 0.15s ease-out',
    padding: 0,
    outline: 'none',
    opacity: validImages.length > 1 ? 1 : 0,
    pointerEvents: validImages.length > 1 ? 'auto' : 'none',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
    willChange: 'transform',
    lineHeight: 0
  };

  // Style pour les points du carrousel
  const dotStyle = (isActive) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: isActive ? '#4a6bce' : 'rgba(255, 255, 255, 0.7)',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.3s ease'
  });

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: '#ffffff' }}>
      <div style={{ display: 'flex', width: '100%', height: '100%', transition: 'transform 0.4s ease-in-out', transform: `translateX(-${currentIndex * 100}%)` }}>
        {validImages.map((src, idx) => (
          <div key={idx} style={{ flex: '0 0 100%', height: '100%' }}>
            <img src={src} alt={`Photo ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ))}
      </div>
      {validImages.length > 1 && (
        <>
          <button 
            style={{ ...carouselButtonBaseStyle, left: 15, fontSize: '16px' }}
            onClick={prev}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(74, 107, 206, 0.9)';
              e.currentTarget.style.transform = 'translate3d(0,-50%,0) scale(1.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
              e.currentTarget.style.transform = 'translate3d(0,-50%,0) scale(1)';
            }}
          >
            <FaChevronLeft />
          </button>
          <button 
            style={{ ...carouselButtonBaseStyle, right: 15, fontSize: '16px' }}
            onClick={next}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(74, 107, 206, 0.9)';
              e.currentTarget.style.transform = 'translate3d(0,-50%,0) scale(1.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
              e.currentTarget.style.transform = 'translate3d(0,-50%,0) scale(1)';
            }}
          >
            <FaChevronRight />
          </button>
          <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6 }}>
            {validImages.map((_, i) => (
              <button
                key={i}
                style={dotStyle(i === currentIndex)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isTransitioning) return;
                  setIsTransitioning(true);
                  setCurrentIndex(i);
                  setTimeout(() => setIsTransitioning(false), 400);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = i === currentIndex ? '#4a6bce' : 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.transform = 'scale(1.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = i === currentIndex ? '#4a6bce' : 'rgba(255, 255, 255, 0.7)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Composant pour afficher le lauréat Trophée Inclus Sport
const TropheeInclusDisplay = ({ laureat, title, photos }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = React.useState(0);
  const [showImageViewer, setShowImageViewer] = React.useState(false);
  const navigate = useNavigate();
  
  const clubNom = laureat?.nom || '';
  const clubSport = laureat?.sport || '';
  const clubLogo = laureat?.logo || null;
  
  // Filtrer les photos valides
  const validPhotos = Array.isArray(photos) ? photos.filter(Boolean) : [];

  React.useEffect(() => {
    setTimeout(() => {
      setIsVisible(true);
    }, 100);
  }, []);
  
  // Défilement automatique dans la popup
  React.useEffect(() => {
    if (validPhotos.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentPhotoIndex(prev => prev === validPhotos.length - 1 ? 0 : prev + 1);
    }, 4000); // Change toutes les 4 secondes
    
    return () => clearInterval(interval);
  }, [currentPhotoIndex, validPhotos.length]);

  // Fonction pour ouvrir la page du club dans un nouvel onglet
  const handleClubClick = (e) => {
    // Pas besoin de stopPropagation ici car nous sommes déjà dans une popup
    // Convertir le nom du club en format URL (tout en minuscule, espaces en tirets)
    const clubUrl = createUrlFriendlyString(clubNom);
    window.open(`/clubs/${clubUrl}`, '_blank');
  };
  
  // Fonctions pour le carrousel
  const prevPhoto = () => {
    setCurrentPhotoIndex(prev => prev === 0 ? validPhotos.length - 1 : prev - 1);
  };
  
  const nextPhoto = () => {
    setCurrentPhotoIndex(prev => prev === validPhotos.length - 1 ? 0 : prev + 1);
  };
  
  // Fonction pour ouvrir le zoom
  const handleImageClick = () => {
    if (validPhotos.length > 0) {
      setShowImageViewer(true);
    }
  };

  if (!laureat) {
    return null;
  }

  const ClubName = styled.h4`
    font-size: 1.3rem;
    font-weight: 700;
    color: #132e57;
    margin: 0 0 8px 0;
    line-height: 1.2;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      color: #4a90e2;
      text-decoration: underline;
    }
  `;

  return (
    <div style={{
      padding: '30px 20px',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: 15,
      margin: '20px 0',
      position: 'relative'
    }}>
      <div style={{
        textAlign: 'center',
        marginBottom: 40
      }}>
        <h3 style={{
          fontSize: '1.4rem',
          fontWeight: 700,
          color: '#132e57',
          margin: '0 0 8px 0'
        }}>
          🏆 Trophée Inclus Sport Super U
        </h3>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px) scale(1)' : 'translateY(30px) scale(0.9)',
            transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 200ms',
            filter: isVisible ? 'blur(0px)' : 'blur(2px)',
            maxWidth: 400,
            textAlign: 'center'
          }}
        >
          {/* Logo du club */}
          <div 
            onClick={handleClubClick}
            style={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: clubLogo ? `url(${clubLogo}) center center no-repeat` : 'linear-gradient(135deg, #2ecc71, #27ae60)',
              backgroundColor: 'white',
              backgroundSize: '70%',
              border: '6px solid #2ecc71',
              marginBottom: 20,
              boxShadow: '0 12px 30px rgba(46, 204, 113, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              fontWeight: 'bold',
              color: 'white',
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 15px 35px rgba(46, 204, 113, 0.4)'
              }
            }}>
            {!clubLogo && clubNom.charAt(0).toUpperCase()}
            
            {/* Badge trophée */}
            <div style={{
              position: 'absolute',
              top: -10,
              right: -10,
              background: 'linear-gradient(135deg, #f39c12, #e67e22)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
              🏆
            </div>
          </div>

          {/* Informations du club */}
          <div style={{
            marginBottom: 20
          }}>
            <ClubName onClick={handleClubClick}>
              {clubNom}
            </ClubName>
            <p style={{
              fontSize: '1rem',
              color: '#2ecc71',
              margin: '0 0 15px 0',
              fontWeight: 600
            }}>
              {clubSport}
            </p>
          </div>

          {/* Badge lauréat */}
          <div style={{
            background: 'linear-gradient(135deg, #2ecc71, #27ae60)',
            color: 'white',
            borderRadius: 25,
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: 700,
            boxShadow: '0 6px 20px rgba(46, 204, 113, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            ⭐ Lauréat du mois
          </div>
          
          {/* Afficher les photos du trophée sous le bouton avec carrousel et zoom */}
          {validPhotos.length > 0 && (
            <div style={{ width: '100%', marginTop: 20 }}>
              <div style={{ 
                width: '100%', 
                height: 300, 
                borderRadius: 10, 
                overflow: 'hidden', 
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                position: 'relative',
                cursor: 'pointer'
              }}
              onClick={handleImageClick}
              >
                {validPhotos.length > 1 ? (
                  <>
                    {/* Carrousel */}
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      transition: 'transform 0.4s ease-in-out',
                      transform: `translateX(-${currentPhotoIndex * 100}%)`
                    }}>
                      {validPhotos.map((photo, idx) => (
                        <div key={idx} style={{ flex: '0 0 100%', height: '100%' }}>
                          <img 
                            src={photo} 
                            alt={`Photo ${idx + 1} - ${clubNom}`} 
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover', 
                              background: '#fff' 
                            }} 
                          />
                        </div>
                      ))}
                    </div>
                    
                    {/* Boutons de navigation */}
                    <button 
                      style={{
                        position: 'absolute',
                        left: 15,
                        top: '50%',
                        transform: 'translate3d(0,-50%,0)',
                        transformOrigin: 'center center',
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        border: 'none',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 20,
                        transition: 'background 0.3s ease, transform 0.15s ease-out',
                        padding: 0,
                        outline: 'none',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                        willChange: 'transform',
                        lineHeight: 0
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        prevPhoto();
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(74, 107, 206, 0.9)';
                        e.currentTarget.style.transform = 'translate3d(0,-50%,0) scale(1.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                        e.currentTarget.style.transform = 'translate3d(0,-50%,0) scale(1)';
                      }}
                    >
                      <FaChevronLeft />
                    </button>
                    
                    <button 
                      style={{
                        position: 'absolute',
                        right: 15,
                        top: '50%',
                        transform: 'translate3d(0,-50%,0)',
                        transformOrigin: 'center center',
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        border: 'none',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 20,
                        transition: 'background 0.3s ease, transform 0.15s ease-out',
                        padding: 0,
                        outline: 'none',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                        willChange: 'transform',
                        lineHeight: 0
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        nextPhoto();
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(74, 107, 206, 0.9)';
                        e.currentTarget.style.transform = 'translate3d(0,-50%,0) scale(1.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                        e.currentTarget.style.transform = 'translate3d(0,-50%,0) scale(1)';
                      }}
                    >
                      <FaChevronRight />
                    </button>
                    
                    {/* Indicateurs */}
                    <div style={{
                      position: 'absolute',
                      bottom: 10,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 8,
                      zIndex: 10
                    }}>
                      {validPhotos.map((_, idx) => (
                        <button
                          key={idx}
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: idx === currentPhotoIndex ? '#4a6bce' : 'rgba(255, 255, 255, 0.7)',
                            cursor: 'pointer',
                            border: 'none',
                            padding: 0,
                            transition: 'all 0.3s ease'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentPhotoIndex(idx);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = idx === currentPhotoIndex ? '#4a6bce' : 'rgba(255, 255, 255, 0.9)';
                            e.currentTarget.style.transform = 'scale(1.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = idx === currentPhotoIndex ? '#4a6bce' : 'rgba(255, 255, 255, 0.7)';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <img 
                    src={validPhotos[0]} 
                    alt={`Photo - ${clubNom}`} 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover', 
                      background: '#fff' 
                    }} 
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Viewer plein écran pour les images */}
      {showImageViewer && validPhotos.length > 0 && (
        <FullScreenImageViewer
          images={validPhotos}
          initialIndex={currentPhotoIndex}
          onClose={() => setShowImageViewer(false)}
        />
      )}
    </div>
  );
};

// Composant pour afficher les Labels 5A
const Label5ADisplay = ({ labels, title }) => {
  const [visibleSections, setVisibleSections] = React.useState({});

  React.useEffect(() => {
    // Faire apparaître les sections progressivement
    const sections = ['or', 'argent', 'bronze'];
    sections.forEach((section, index) => {
      setTimeout(() => {
        setVisibleSections(prev => ({ ...prev, [section]: true }));
      }, 300 * (index + 1));
    });
  }, []);

  if (!labels) return null;

  const getLabelColor = (type) => {
    switch (type) {
      case 'or': return { primary: '#FFD700', secondary: '#FFA500', gradient: 'linear-gradient(135deg, #FFD700, #FFA500)' };
      case 'argent': return { primary: '#C0C0C0', secondary: '#A8A8A8', gradient: 'linear-gradient(135deg, #E8E8E8, #C0C0C0)' };
      case 'bronze': return { primary: '#CD7F32', secondary: '#B8860B', gradient: 'linear-gradient(135deg, #CD7F32, #B8860B)' };
      default: return { primary: '#1a73e8', secondary: '#1557b0', gradient: 'linear-gradient(135deg, #1a73e8, #1557b0)' };
    }
  };

  const getLabelIcon = (type) => {
    switch (type) {
      case 'or': return '🥇';
      case 'argent': return '🥈';
      case 'bronze': return '🥉';
      default: return '🏅';
    }
  };

  const getLabelTitle = (type) => {
    switch (type) {
      case 'or': return 'Label 5A OR';
      case 'argent': return 'Label 5A ARGENT';
      case 'bronze': return 'Label 5A BRONZE';
      default: return 'Label 5A';
    }
  };

  const renderLabelSection = (type, clubs) => {
    if (!clubs || clubs.length === 0) return null;
    
    const colors = getLabelColor(type);
    const isVisible = visibleSections[type];

    return (
      <div
        key={type}
        style={{
          marginBottom: 40,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
          transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* En-tête de section */}
        <div style={{
          textAlign: 'center',
          marginBottom: 30,
          padding: '20px',
          background: colors.gradient,
          borderRadius: 15,
          color: type === 'argent' ? '#333' : 'white',
          boxShadow: `0 8px 25px ${colors.primary}40`
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12
          }}>
            <span style={{ fontSize: '2rem' }}>{getLabelIcon(type)}</span>
            {getLabelTitle(type)}
          </h3>
          <p style={{
            margin: 0,
            fontSize: '1rem',
            opacity: 0.9
          }}>
            {clubs.length} club{clubs.length > 1 ? 's' : ''} récompensé{clubs.length > 1 ? 's' : ''}
          </p>
        </div>

        {/* Grille des clubs */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 20,
          padding: '0 10px'
        }}>
          {clubs.map((club, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                borderRadius: 15,
                padding: 20,
                boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                border: `3px solid ${colors.primary}`,
                position: 'relative',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = `0 12px 30px ${colors.primary}30`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)';
              }}
            >
              {/* Badge label */}
              <div style={{
                position: 'absolute',
                top: -15,
                right: 15,
                background: colors.gradient,
                borderRadius: '50%',
                width: 50,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                boxShadow: `0 4px 12px ${colors.primary}40`
              }}>
                {getLabelIcon(type)}
              </div>

              {/* Logo du club */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 15
              }}>
                <div style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: club.logo ? `url(${club.logo})` : colors.gradient,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  marginRight: 15,
                  border: `2px solid ${colors.primary}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {!club.logo && club.nom.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 style={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: '#132e57',
                    margin: '0 0 4px 0',
                    lineHeight: 1.2
                  }}>
                    {club.nom}
                  </h4>
                  <p style={{
                    fontSize: '0.9rem',
                    color: colors.primary,
                    margin: 0,
                    fontWeight: 600
                  }}>
                    {club.sport}
                  </p>
                </div>
              </div>

              {/* Description */}
              <p style={{
                fontSize: '0.85rem',
                color: '#64748b',
                margin: 0,
                lineHeight: 1.4,
                fontStyle: 'italic'
              }}>
                {club.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      padding: '30px 20px',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: 15,
      margin: '20px 0'
    }}>
      <div style={{
        textAlign: 'center',
        marginBottom: 40
      }}>
        <h3 style={{
          fontSize: '1.6rem',
          fontWeight: 700,
          color: '#132e57',
          margin: '0 0 8px 0'
        }}>
          {title}
        </h3>
        <p style={{
          fontSize: '1rem',
          color: '#64748b',
          margin: 0
        }}>
          Reconnaissance de l'excellence sportive et de la qualité d'accueil
        </p>
      </div>

      {renderLabelSection('or', labels.or)}
      {renderLabelSection('argent', labels.argent)}
      {renderLabelSection('bronze', labels.bronze)}
    </div>
  );
};

// Composant pour les vignettes standard (actualités, etc.)
const StandardCard = ({ image, title, category, excerpt, onClick }) => (
  <Card onClick={onClick}>
    <CardImage style={{ background: '#f5f5f5' }}>
      <img 
        src={image} 
        alt={title} 
        style={{
          objectFit: 'cover',
          padding: '0'
        }}
      />
    </CardImage>
    <CardBody>
      <CardCategory>{category}</CardCategory>
      <CardTitle>{title}</CardTitle>
      <CardText>{excerpt}</CardText>
    </CardBody>
  </Card>
);

// Composant pour les vignettes Club du Mois et Trophée Inclus Sport
const SpecialCard = ({ image, title, category, excerpt, onClick }) => {
  const isTropheeInclusSport = category === "Trophée Inclus sport Super U" || title.toLowerCase().includes('trophée inclus sport');
  if (isTropheeInclusSport) {
    return (
      <TropheeInclusSportCard onClick={onClick}>
        <CardImage style={{ background: '#ffffff' }}>
          <img 
            src={image} 
            alt={title} 
            style={{
              objectFit: 'contain',
              padding: '10px'
            }}
          />
        </CardImage>
        <TropheeInclusSportCardBody>
          <CardCategory>{category}</CardCategory>
          <CardTitle>{title}</CardTitle>
          <CardText>{excerpt}</CardText>
        </TropheeInclusSportCardBody>
      </TropheeInclusSportCard>
    );
  }
  // Sinon, carte classique
  return (
    <Card>
      <div onClick={onClick} style={{ cursor: 'pointer' }}>
        <CardImage style={{ background: '#ffffff' }}>
          <img 
            src={image} 
            alt={title} 
            style={{
              objectFit: 'contain',
              padding: '10px'
            }}
          />
        </CardImage>
        <CardBody>
          <CardCategory>{category}</CardCategory>
          <CardTitle>{title}</CardTitle>
          <CardText>{excerpt}</CardText>
        </CardBody>
      </div>
    </Card>
  );
};

// Popup pour les vignettes standard
const StandardPopup = ({ title, image, category, excerpt, onClose }) => (
  <PopupOverlay onClick={onClose}>
    <PopupContent onClick={e => e.stopPropagation()}>
      <PopupCloseButton onClick={onClose}><FaTimes /></PopupCloseButton>
      <PopupTitle>{title}</PopupTitle>
      <PopupImage>
        <img src={image} alt={title} />
      </PopupImage>
      <div style={{ marginBottom: '20px' }}>
        <PopupInfo><FaList /> <span>{category}</span></PopupInfo>
        <PopupInfo><FaUser /> <span>Super U</span></PopupInfo>
      </div>
      <PopupDescription>{excerpt}</PopupDescription>
    </PopupContent>
  </PopupOverlay>
);

// Popup pour Club du Mois
const ClubDuMoisPopup = ({ title, podium, onClose }) => (
  <PopupOverlay onClick={onClose}>
    <PopupContent onClick={e => e.stopPropagation()}>
      <PopupCloseButton onClick={onClose}><FaTimes /></PopupCloseButton>
      <PopupTitle style={{ fontWeight: 'bold' }}>{title}</PopupTitle>
      <div style={{ marginTop: 30 }}>
        <ClubDuMoisPodiumDisplay podium={podium} title={title} />
      </div>
    </PopupContent>
  </PopupOverlay>
);

// Popup spécifique pour Trophée Inclus Sport
const TropheeInclusSportPopup = ({ title, laureat, photos, onClose }) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <PopupOverlay onClick={handleOverlayClick}>
      <PopupContent onClick={e => e.stopPropagation()}>
        <PopupCloseButton onClick={onClose}><FaTimes /></PopupCloseButton>
        <PopupTitle style={{ fontWeight: 'bold' }}>{title}</PopupTitle>
        <div style={{ marginTop: 30 }}>
          <TropheeInclusDisplay laureat={laureat} title={title} photos={photos} />
        </div>
      </PopupContent>
    </PopupOverlay>
  );
};

// Popup pour Label 5A
const Label5APopup = ({ title, labels, onClose }) => (
  <PopupOverlay onClick={onClose}>
    <PopupContent onClick={e => e.stopPropagation()}>
      <PopupCloseButton onClick={onClose}><FaTimes /></PopupCloseButton>
      <PopupTitle style={{ fontWeight: 'bold' }}>{title}</PopupTitle>
      <div style={{ marginTop: 30 }}>
        <Label5ADisplay labels={labels} title={title} />
      </div>
    </PopupContent>
  </PopupOverlay>
);

// Composant Carrousel Club du Mois
const ClubDuMoisCarousel = ({ podium }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Filtrer les clubs valides avec logo
  const validClubs = podium ? podium.filter(club => club && club.logo) : [];
  
  // Si pas assez d'images, utiliser une image par défaut
  const defaultImage = "/images/clubs/club-default.jpg";
  const images = validClubs.length > 0 
    ? validClubs.map(club => club.logo)
    : [defaultImage];
  
  const prevImage = (e) => {
    if (e) e.stopPropagation();
    
    if (isTransitioning || images.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    setTimeout(() => setIsTransitioning(false), 500);
  };
  
  const nextImage = (e) => {
    if (e) e.stopPropagation();
    
    if (isTransitioning || images.length <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    setTimeout(() => setIsTransitioning(false), 500);
  };
  
  // Défilement automatique
  useEffect(() => {
    if (images.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
      setTimeout(() => setIsTransitioning(false), 500);
    }, 4000); // Change toutes les 4 secondes
    
    return () => clearInterval(interval);
  }, [currentIndex, images.length]);
  
  // Style pour le conteneur du carrousel Club du Mois
  const carouselContainerStyle = {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: '#ffffff'
  };
  
  // Style pour la piste du carrousel
  const carouselTrackStyle = {
    display: 'flex',
    width: '100%',
    height: '100%',
    transition: 'transform 0.5s ease-in-out',
    transform: `translateX(-${currentIndex * 100}%)`
  };
  
  // Style pour les boutons du carrousel Club du Mois
  const carouselButtonStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    border: 'none',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 20,
    transition: 'all 0.3s ease',
    padding: 0,
    outline: 'none',
    opacity: images.length > 1 ? 1 : 0,
    pointerEvents: images.length > 1 ? 'auto' : 'none',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)'
  };
  
  // Style pour les indicateurs du carrousel
  const dotsContainerStyle = {
    position: 'absolute',
    bottom: '10px',
    left: '0',
    right: '0',
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    opacity: images.length > 1 ? 1 : 0
  };
  
  const dotStyle = (isActive) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: isActive ? '#4a6bce' : 'rgba(255, 255, 255, 0.7)',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.3s ease'
  });
  
  // Fonction pour obtenir les couleurs selon la position
  const getMedalColor = (position) => {
    switch (position) {
      case 1: return { primary: '#FFD700', secondary: '#FFA500', gradient: 'linear-gradient(135deg, #FFD700, #FFA500)' };
      case 2: return { primary: '#C0C0C0', secondary: '#A8A8A8', gradient: 'linear-gradient(135deg, #E8E8E8, #C0C0C0)' };
      case 3: return { primary: '#CD7F32', secondary: '#B8860B', gradient: 'linear-gradient(135deg, #CD7F32, #B8860B)' };
      default: return { primary: '#1a73e8', secondary: '#1557b0', gradient: 'linear-gradient(135deg, #1a73e8, #1557b0)' };
    }
  };

  // Fonction pour obtenir l'emoji de médaille
  const getMedalEmoji = (position) => {
    switch (position) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏅';
    }
  };

  return (
    <div style={carouselContainerStyle}>
      <div style={carouselTrackStyle}>
        {images.map((img, index) => {
          const club = validClubs[index];
          const medalColor = club ? getMedalColor(club.position) : null;
          
          return (
            <div
              key={index}
              style={{
                flex: '0 0 100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px',
                position: 'relative'
              }}
            >
              <img 
                src={img} 
                alt={club ? `${club.nom} - Position ${club.position}` : "Club du mois"}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
              
              {/* Badge de médaille */}
              {club && medalColor && (
                <div style={{
                  position: 'absolute',
                  top: 15,
                  right: 15,
                  background: medalColor.gradient,
                  borderRadius: '50%',
                  width: 45,
                  height: 45,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.4rem',
                  boxShadow: `0 4px 12px ${medalColor.primary}40`,
                  border: '3px solid white',
                  zIndex: 5
                }}>
                  {getMedalEmoji(club.position)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <button 
        style={{...carouselButtonStyle, left: '15px', fontSize: '16px'}}
        onClick={prevImage}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(74, 107, 206, 0.9)';
          e.currentTarget.style.transform = 'scale(1.1) translateY(-50%)';
          e.currentTarget.style.transformOrigin = 'center center';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
          e.currentTarget.style.transform = 'scale(1) translateY(-50%)';
          e.currentTarget.style.transformOrigin = 'center center';
        }}
      >
        <FaChevronLeft />
      </button>
      
      <button 
        style={{...carouselButtonStyle, right: '15px', fontSize: '16px'}}
        onClick={nextImage}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(74, 107, 206, 0.9)';
          e.currentTarget.style.transform = 'scale(1.1) translateY(-50%)';
          e.currentTarget.style.transformOrigin = 'center center';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
          e.currentTarget.style.transform = 'scale(1) translateY(-50%)';
          e.currentTarget.style.transformOrigin = 'center center';
        }}
      >
        <FaChevronRight />
      </button>
      
      <div style={dotsContainerStyle}>
        {images.map((_, index) => (
          <button
            key={index}
            style={dotStyle(index === currentIndex)}
            onClick={(e) => {
              e.stopPropagation();
              setIsTransitioning(true);
              setCurrentIndex(index);
              setTimeout(() => setIsTransitioning(false), 500);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = index === currentIndex ? '#4a6bce' : 'rgba(255, 255, 255, 0.9)';
              e.currentTarget.style.transform = 'scale(1.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = index === currentIndex ? '#4a6bce' : 'rgba(255, 255, 255, 0.7)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Composant principal ArticleCard
const ArticleCard = ({ image, photos, title, category, excerpt, podium, laureat, labels, disablePopup, forcePopupOpen, onClose, onClick }) => {
  const [showPopup, setShowPopup] = useState(false);

  const openPopup = () => {
    // Si disablePopup est activé ET qu'il y a une fonction onClick externe, l'utiliser
    if (disablePopup && onClick) {
      onClick();
      return;
    }
    // Sinon, si disablePopup est activé sans onClick externe, ne rien faire
    if (disablePopup) return;
    
    setShowPopup(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setShowPopup(false);
    document.body.style.overflow = 'auto';
  };

  // Détermine le type de contenu
  const isClubDuMois = category === "Club du mois Decathlon" || title.toLowerCase().includes('club du mois décathlon');
  const isTropheeInclusSport = category === "Trophée Inclus sport Super U" || title.toLowerCase().includes('trophée inclus sport');
  const isLabel5A = category === "Label 5A" || title.toLowerCase().includes('label 5a');
  const isFestivalPicantins = category === "Festival des Picantins" || title.toLowerCase().includes('picantin');

  // Si la popup est forcée d'être ouverte
  if (forcePopupOpen) {
    if (isClubDuMois) {
      return <ClubDuMoisPopup title={title} podium={podium} onClose={onClose || closePopup} />;
    }
    if (isTropheeInclusSport) {
      return <TropheeInclusSportPopup title={title} laureat={laureat} photos={photos} onClose={onClose || closePopup} />;
    }
    if (isLabel5A) {
      return <Label5APopup title={title} labels={labels} onClose={onClose || closePopup} />;
    }
    return <StandardPopup title={title} image={image} category={category} excerpt={excerpt} onClose={onClose || closePopup} />;
  }

  return (
    <>
      <Card onClick={openPopup}>
        <CardImage style={{
          background: (isClubDuMois || isTropheeInclusSport) ? '#ffffff' : '#f5f5f5'
        }}>
          {isClubDuMois && podium ? (
            <ClubDuMoisCarousel podium={podium} />
          ) : (
            isTropheeInclusSport && Array.isArray(photos) && photos.length > 1 ? (
              <TISCarousel images={photos} />
            ) : (
              // Fallback robuste pour TIS: 1) première photo 2) image prop 3) logo du club
              (() => {
                const tisImages = Array.isArray(photos) ? photos.filter(Boolean) : [];
                const displayImage = isTropheeInclusSport ? (tisImages[0] || image || (laureat && laureat.logo) || undefined) : image;
                // Déterminer si c'est une photo TIS (du champ Photo) ou un logo de club (fallback)
                // C'est un logo de club seulement si: pas de photos ET l'image affichée est le logo du club
                const hasPhoto = tisImages.length > 0;
                const isClubLogo = isTropheeInclusSport && !hasPhoto;
                
                // Logs de diagnostic supprimés
                
                return (
              <img 
                src={displayImage} 
                alt={title} 
                style={{
                  objectFit: isClubLogo ? 'contain' : 'cover',
                  padding: isClubLogo ? '10px' : '0'
                }}
              />
                );
              })()
            )
          )}
        </CardImage>
        <CardBody>
          <CardCategory>{category}</CardCategory>
          <CardTitle>{title}</CardTitle>
          <CardText 
            $isTrophee={isTropheeInclusSport}
            style={isFestivalPicantins ? {
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxHeight: '4.5em', // 3 lignes * 1.5em
            } : {}}
          >
            {excerpt}
          </CardText>
        </CardBody>
      </Card>

      {showPopup && !disablePopup && (
        <>
          {isClubDuMois && (
            <ClubDuMoisPopup title={title} podium={podium} onClose={closePopup} />
          )}
          {isTropheeInclusSport && (
            <TropheeInclusSportPopup title={title} laureat={laureat} photos={photos} onClose={closePopup} />
          )}
          {isLabel5A && (
            <Label5APopup title={title} labels={labels} onClose={closePopup} />
          )}
          {!isClubDuMois && !isTropheeInclusSport && !isLabel5A && (
            <StandardPopup 
              title={title}
              image={image}
              category={category}
              excerpt={excerpt}
              onClose={closePopup}
            />
          )}
        </>
      )}
    </>
  );
};

export default ArticleCard;
export { SimpleOsarcMagCard }; 